import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { findCity, SERVICES, BUSINESS } from "@/lib/site-data";

function cityFaqs(cityName: string, region: string) {
  return [
    {
      q: `Do you only work with contractors based in ${cityName}?`,
      a: `No — we work with trades contractors who serve ${cityName}, ${region}, even if their shop is in a neighboring city. What matters is the service area you want to rank in.`,
    },
    {
      q: `How long until I rank in the ${cityName} Map Pack?`,
      a: `Most trades clients see Map Pack movement in ${cityName} within 60–90 days, with meaningful lead volume by month 4–6. Competition density and Google Business Profile maturity drive the timeline.`,
    },
    {
      q: `Which trades do you take on in ${cityName}?`,
      a: `HVAC, plumbing, electrical, roofing, remodeling, garage doors, and most home-service trades. We typically take one contractor per trade per city to avoid client conflicts.`,
    },
    {
      q: `Do you offer Google Ads management for ${cityName} contractors too?`,
      a: `Yes. We run Google Search, Local Service Ads, and Performance Max alongside SEO so you have paid lead flow while organic rankings build.`,
    },
  ];
}

export const Route = createFileRoute("/service-areas/$city")({
  loader: ({ params }) => {
    const city = findCity(params.city);
    if (!city) throw notFound();
    return { city };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Area not found" }] };
    const c = loaderData.city;
    const title = `Trades SEO in ${c.name}, ${c.region} | ${BUSINESS.shortName}`;
    const description = `Local SEO, Google Ads, and websites for HVAC, plumbing, electrical, and home-service contractors in ${c.name}, ${c.region}.`;
    const faqs = cityFaqs(c.name, c.region);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: `/service-areas/${params.city}` },
      ],
      links: [{ rel: "canonical", href: `https://lehitradessaeo.com/service-areas/${params.city}` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
          "@type": "ProfessionalService",
            name: BUSINESS.name,
            telephone: BUSINESS.phone,
            areaServed: {
              "@type": "City",
              name: `${c.name}, ${c.region}`,
            },
            address: {
              "@type": "PostalAddress",
              streetAddress: BUSINESS.address.street,
              addressLocality: BUSINESS.address.locality,
              addressRegion: BUSINESS.address.region,
              postalCode: BUSINESS.address.postalCode,
              addressCountry: BUSINESS.address.country,
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-20 text-center">
      <h1 className="text-3xl font-black">Area not found</h1>
      <p className="mt-2 text-muted-foreground">
        We don't list that city. <Link to="/service-areas" className="underline">See all areas</Link>.
      </p>
    </main>
  ),
  component: CityPage,
});

function CityPage() {
  const { city } = Route.useLoaderData();
  return (
    <main>
      <section className="border-b border-border">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
            Service Area
          </p>
          <h1 className="mt-4 font-display text-5xl tracking-tight md:text-6xl">
            Trades SEO in {city.name}, {city.region}
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-muted-foreground">{city.blurb}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Get a free audit
            </Link>
            <a
              href={BUSINESS.phoneHref}
              className="rounded-full px-6 py-3 text-sm font-medium text-foreground hover:bg-secondary"
            >
              Call {BUSINESS.phone}
            </a>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-16">
        <h2 className="font-display text-3xl tracking-tight">
          Services for {city.name} contractors
        </h2>
        <div className="mt-8 grid gap-4 md:grid-cols-2">
          {SERVICES.map((s) => (
            <Link
              key={s.slug}
              to="/services/$service"
              params={{ service: s.slug }}
              className="block rounded-2xl border border-border bg-card p-6 transition hover:border-primary/40 hover:shadow-sm"
            >
              <div className="font-display text-xl">
                {s.short} for {city.name} contractors
              </div>
              <p className="mt-2 text-sm text-muted-foreground">{s.tagline}</p>
            </Link>
          ))}
        </div>

        <h2 className="mt-16 font-display text-3xl tracking-tight">
          Neighborhoods our clients rank in
        </h2>
        <ul className="mt-6 flex flex-wrap gap-2">
          {city.neighborhoods.map((n: string) => (
            <li
              key={n}
              className="rounded-full border border-border bg-card px-4 py-1.5 text-sm text-muted-foreground"
            >
              {n}
            </li>
          ))}
        </ul>

        <h2 className="mt-16 font-display text-3xl tracking-tight">
          {city.name} trades SEO — frequently asked questions
        </h2>
        <div className="mt-6 space-y-4">
          {cityFaqs(city.name, city.region).map((f) => (
            <details
              key={f.q}
              className="group rounded-2xl border border-border bg-card p-5"
            >
              <summary className="cursor-pointer list-none font-medium text-foreground">
                {f.q}
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                {f.a}
              </p>
            </details>
          ))}
        </div>
      </section>
    </main>
  );
}